import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Theme } from "@/store/enums/ThemeEnum";
import { NodeType } from "@/core/services/api/enums/NodeType";
import { LiveChatType } from "@/interfaces/common/LiveChatType";
import { supportEmail } from "@/core/helpers/config";

export interface StoreInfo {
  classes: {
    header?: Array<string>;
    headerContainer?: Array<string>;
    headerMobile?: Array<string>;
    headerMenu?: Array<string>;
    aside?: Array<string>;
    asideMenu?: Array<string>;
    asideToggle?: Array<string>;
    toolbar?: Array<string>;
    toolbarContainer?: Array<string>;
    content?: Array<string>;
    contentContainer?: Array<string>;
    footerContainer?: Array<string>;
    sidebar?: Array<string>;
    pageTitle?: Array<string>;
  };
}

@Module
export default class BodyModule extends VuexModule implements StoreInfo {
  classes = {};
  defaultTheme: Theme = Theme.IBANFLEX;

  get getRelease(): string {
    return process.env.VUE_APP_RELEASE as string;
  }

  get getSalesIqWidget(): null | string {
    return process.env.VUE_APP_LIVECHAT_SALES_IQ_WIDGET_CODE || null;
  }

  get getLiveChatScriptSrcSalesIq(): null | string {
    return process.env.VUE_APP_LIVECHAT_SRC_SALES_IQ || null;
  }

  get getLiveChatScriptSrcRocket(): null | string {
    return process.env.VUE_APP_LIVECHAT_SRC_ROCKET || null;
  }

  get getLiveChatScriptRocketUrl(): null | string {
    return process.env.VUE_APP_LIVECHAT_ROCKET_URL || null;
  }

  get getLiveChatType(): null | LiveChatType {
    const livechat = process.env.VUE_APP_LIVECHAT || null;
    if (!livechat) {
      return null;
    }

    return LiveChatType[livechat] as LiveChatType;
  }

  /**
   * Get current page title
   * @returns string
   */
  get getClasses() {
    return (position: string) => {
      if (typeof position !== "undefined") {
        return this.classes[position];
      }
      return this.classes;
    };
  }

  get isThemeDefault() {
    return this.isTheme(this.defaultTheme);
  }

  get isThemeKatbit() {
    return this.isTheme(Theme.KATBIT);
  }

  get isThemeIbanflex() {
    return this.isTheme(Theme.IBANFLEX);
  }

  get theme(): string {
    return process.env.VUE_APP_THEME as string;
  }

  get nodeType(): NodeType {
    return process.env.VUE_APP_NODE as NodeType;
  }

  get isRootNode(): boolean {
    return NodeType.ROOT === this.nodeType;
  }

  get isSubNode(): boolean {
    return NodeType.SUB === this.nodeType;
  }

  get currentPortal(): string {
    return process.env.VUE_APP_CURRENT_PORTAL as string;
  }

  get isFastpayCurrentPortal(): boolean {
    return this.currentPortal === "fastpay";
  }

  get isIbanflexCurrentPortal(): boolean {
    return this.currentPortal === "ibanflex";
  }

  get isBreadxpayCurrentPortal(): boolean {
    return this.currentPortal === "breadxpay";
  }

  get appName(): string {
    return process.env.VUE_APP_NAME as string;
  }

  get isTheme() {
    return (theme: Theme): boolean => theme === this.theme;
  }

  get getSupportEmail() {
    return supportEmail.value;
  }

  @Mutation
  [Mutations.THEME_CLASSNAME_BY_POSITION_SET](payload) {
    const { position, className } = payload;
    if (!this.classes[position]) {
      this.classes[position] = [];
    }
    this.classes[position].push(className);
  }

  @Action
  [Actions.THEME_BODY_CLASSNAME_ADD](className) {
    document.body.classList.add(className);
  }

  @Action
  [Actions.THEME_ELEMENT_CLASSNAME_TOGGLE]({
    targetSelector,
    className,
  }: {
    targetSelector: string;
    className: string | Array<string>;
  }) {
    const element = document.querySelector(targetSelector);
    if (!element) {
      return;
    }

    if (typeof className === "string") {
      if (className.includes(" ")) {
        className = className.split(" ");
      } else {
        className = [className];
      }
    }
    className.forEach((classItem: string) => {
      return element.classList.toggle(classItem);
    });
  }

  @Action
  [Actions.THEME_LIVECHAT_REMOVE]() {
    const rocketChatScript = document.getElementById("rocket-support");
    if (rocketChatScript) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.RocketChat(function () {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.hideWidget();
      });

      rocketChatScript.remove();
    }
    const salesIqChatScript = document.getElementById("zsiqscript");
    if (salesIqChatScript) {
      salesIqChatScript.remove();
    }
  }

  @Action
  [Actions.THEME_LIVECHAT_INIT]() {
    console.log("THEME_LIVECHAT_INIT");
    if (!this.context.getters.getUser) {
      return;
    }
    const settings = this.context.getters.getUserLiveChatSettings;
    const type = this.context.getters.getLiveChatType;
    if (type === LiveChatType.ROCKET) {
      if (!("RocketChat" in window)) {
        return;
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.RocketChat(function () {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.registerGuest({
          token: settings.token,
          name: settings.name,
          email: settings.email,
          customFields: [
            {
              key: "company_id",
              //convert to string
              value: String(settings.company_id),
              overwrite: true,
            },
            {
              key: "user_id",
              value: String(settings.user_id),
              overwrite: true,
            },
            {
              key: "email",
              value: String(settings.email),
              overwrite: true,
            },
          ],
        });
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.showWidget();
      });
      return;
    }
  }

  @Action
  [Actions.THEME_BODY_CLASSNAME_REMOVE](className) {
    document.body.classList.remove(className);
  }

  @Action
  [Actions.THEME_BODY_ATTRIBUTE_ADD](payload) {
    const { qualifiedName, value } = payload;
    document.body.setAttribute(qualifiedName, value);
  }

  @Action
  [Actions.THEME_BODY_ATTRIBUTE_REMOVE](payload) {
    const { qualifiedName } = payload;
    document.body.removeAttribute(qualifiedName);
  }

  @Action
  [Actions.THEME_CLASSNAME_ADD](payload) {
    this.context.commit(Mutations.THEME_CLASSNAME_BY_POSITION_SET, payload);
  }
}
